<script lang="ts">
import type { PropType } from "vue"
import type {
  HidingToastEventContext,
  ToastEventContext,
  ToastInstance,
  ToastShowOptions,
  ToastVariant,
} from "@/types"
import { defineComponent, Fragment, h, onMounted, ref, Teleport, useAttrs, useSlots } from "vue"
import { StyledToast } from "@/components/ui"
import { useToast } from "@/composables"

export default defineComponent({
  props: {
    duration: {
      type: Number,
      default: 10000,
    },
    singleton: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String as PropType<ToastVariant>,
      default: "default",
    },
  },
  emits: {
    showing: () => true,
    shown: (context: ToastEventContext) => !!context,
    hiding: (context: HidingToastEventContext) => !!context,
    hidden: (context: HidingToastEventContext) => !!context,
  },
  setup(props, { emit, expose }) {
    const attrs = useAttrs()
    const slots = useSlots()

    const { scope } = useToast()

    const groupId = ref()

    onMounted(() => {
      groupId.value = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    })

    const hide = () => {
      emit("hiding", {})

      scope.removeAll(groupId.value)

      emit("hidden", {})
    }

    const showToast = (options: ToastShowOptions = {}) => {
      emit("showing")

      if (props.singleton && scope.toasts.value.length > 0) {
        const toastInstance = scope.toasts.value[0]

        clearTimeout(toastInstance.timer)

        toastInstance.timer = window.setTimeout(() => {
          scope.remove(toastInstance)
        }, props.duration)

        return toastInstance
      }

      const toastInstance: ToastInstance = {
        groupId: groupId.value,
        title: options.title,
        icon: options.icon,
        content: options.content,
        attrs: { ...attrs, variant: props.variant },
        slot: options.title || options.content || options.icon ? undefined : slots.default,
        timer: window.setTimeout(() => {
          emit("hiding", {
            toast: toastInstance,
            dismissed: false,
          })

          scope.remove(toastInstance)

          emit("hidden", {
            toast: toastInstance,
            dismissed: false,
          })
        }, props.duration),
      }

      toastInstance.hide = () => {
        emit("hiding", {
          toast: toastInstance,
          dismissed: true,
        })

        scope.remove(toastInstance)

        clearTimeout(toastInstance.timer)

        emit("hidden", {
          toast: toastInstance,
          dismissed: true,
        })
      }

      scope.add(toastInstance)
      emit("shown", {
        toast: toastInstance,
      })
    }

    expose({ showToast, hide })

    if (scope.shared)
      return () => null

    return () => {
      return scope.toasts.value.length > 0
        ? h(Teleport, { to: "body" }, [
            h("div", { class: "gpx-toast-container" }, [
              h(
                Fragment,
                scope.toasts.value.map((toast) => {
                  return h(
                    StyledToast,
                    toast.slot
                      ? { ...toast.attrs, toast, onHide: (toast: ToastInstance) => toast.hide?.() }
                      : {
                          toast,
                          ...toast.attrs,
                          textContent: toast.content,
                          title: toast.title,
                          icon: toast.icon,
                          onHide: (toast: ToastInstance) => toast.hide?.(),
                        },
                    toast.slot
                      ? () => toast.slot?.({ content: toast.content, title: toast.title, icon: toast.icon })
                      : undefined,
                  )
                }),
              ),
            ]),
          ])
        : null
    }
  },
})
</script>
